import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  getEntity,
  addEntities,
  updateEntities,
  deleteEntities,
} from '@ngneat/elf-entities';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IAddVideo, IDeleteVideo, IVideo } from './video.interface';
import { IEditVideo } from 'src/app/game/lvl1/video/edit-video/edit-video.component';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl1-video';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IVideo, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class VideoCoreService {
  private _loading = false;

  /**
   * @name videoList$
   * @description emits stored list of the companies
   */

  videoList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 300);
    } else {
      this.refresh();
    }
  }

  public getVideos(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`videos/list`, params || {})
      .subscribe((data: IVideoListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addVideo(data: IAddVideo): Observable<any> {
    this._loading = true;
    return this._api.post('videos/add', data, {}).pipe(
      tap((video: IVideo) => {
        store.update(addEntities(video), setProp('lastRefreshed', new Date()));
        this._loading = false;
      }),
    );
  }

  public editVideo(data: IEditVideo): Observable<any> {
    this._loading = true;
    return this._api.put('videos/edit', data, {}).pipe(
      map(() => {
        store.update(
          updateEntities(data['_id'], data),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public deleteVideo(data: IDeleteVideo): Observable<any> {
    this._loading = true;
    return this._api.delete('videos', { body: data }).pipe(
      map(() => {
        store.update(
          deleteEntities(data['_id']),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public getVideoInfo(id: string): IVideo {
    const video = store.query(getEntity(id));
    return video;
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  public resolveVideoId(video: string | IVideo): string {
    let videoId: string | IVideo = '';
    if (video) {
      videoId = video;
    }
    if (video['_id']) {
      videoId = video['_id'];
    }
    return videoId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getVideos();
    }
  }
}

interface IVideoListResponse {
  list: IVideo[];
  totalCount: number;
}
